<script setup lang="ts">
import {
  callWithErrorHandling,
  getCurrentInstance,
  onMounted,
  onBeforeUnmount,
  provide,
  ref,
  watchEffect,
} from "vue";
import { randomID } from "../../../../../utils/helpers";
import MButton from "../../../../medicus/MButton";
import PatientBanner from "../../../../medicus/PatientBanner";
import { useFocusTrap } from "../../../../../composables/useFocusTrap";
import { listen } from "../../../../../utils/bus";
import { useDebounce } from "vue-composable";

interface PropOptions {
  ariaLabelledBy?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
  modalSize?: "small" | "large" | "form" | "full";
}

const props = defineProps<{
  title?: string;
  patientId?: string;
  type?: "tab" | "dialog" | "small" | "form" | "large" | "full";
  bgGrey?: boolean;
  modalSize?: "small" | "large" | "form" | "full";

  options?: PropOptions;
}>();

const emit = defineEmits({
  close: () => true,
});

const type = ref(props.type);
const modalTitle = ref(props.title);
const modalSize = ref(props.modalSize);
const padding = ref(true);
const bgGrey = ref(props.bgGrey);

watchEffect(() => {
  // allows us to set the title from where we open the modal.
  if (props.options?.props?.modalTitleOverride) {
    modalTitle.value = props.options?.props?.modalTitleOverride;
  } else {
    modalTitle.value = props.title;
  }
});

provide("setModalType", (t: "tab" | "dialog" | "small" | "form" | "large" | "full") => {
  type.value = t;
});
provide("setModalSize", (t: "small" | "large" | "form" | "full") => {
  modalSize.value = t;
});
provide("removePadding", (s: boolean) => {
  padding.value = !s;
});
provide("setBgGrey", (s: boolean) => {
  bgGrey.value = s;
});

provide("setModalSmall", () => {
  // decrepated, use setModalType('small')
  type.value = "small";
  // TODO remove this in the future, until setModalSmall is used
  if (import.meta.env.DEV) {
    const warnHandler = getCurrentInstance()?.appContext.app.config.warnHandler;
    if (warnHandler) {
      warnHandler(
        'setModalSmall(true) is deprecated, use setModalType("small")',
        getCurrentInstance().proxy,
        "",
      );
    } else {
      callWithErrorHandling(
        () => {
          throw new Error("setModalSmall(true) is deprecated, use setModalType('small')");
        },
        null,
        0,
      );
    }
  }
});

provide("reloadModal", () => {
  // decrepated, use setModalType('small')
  // TODO remove this in the future, until reloadModal is used
  if (import.meta.env.DEV) {
    const warnHandler = getCurrentInstance()?.appContext.app.config.warnHandler;
    if (warnHandler) {
      warnHandler(
        'reloadModal(true) is deprecated, use setModalType("small")',
        getCurrentInstance().proxy,
        "",
      );
    } else {
      callWithErrorHandling(
        () => {
          throw new Error("reloadModal(true) is deprecated, use setModalType('small')");
        },
        null,
        0,
      );
    }
  }
});

function handleCloseBtnClicked() {
  emit("close");
}

const labelId = props.options?.ariaLabelledBy ? randomID("modal-label") : null;

const modalRef = ref<HTMLElement | null>(null);
const focusTrap = useFocusTrap(modalRef, props.options?.autoFocus, true);

listen(
  "reloadData",
  useDebounce(() => {
    focusTrap.resetOnCurrentRef(modalRef);
  }, 300),
);

function setTitle(e: Event) {
  const ce = e as CustomEvent<{ id?: string; title: string }>;
  if (!ce.detail) return;
  modalTitle.value = ce.detail.title;
}

// Allows us to change a title of an existing Modal/
onMounted(() => {
  document.addEventListener("modal:setTitle", setTitle);
});

onBeforeUnmount(() => {
  document.removeEventListener("modal:setTitle", setTitle);
});

// modal:setTitle called with —
// document.dispatchEvent(
//   new CustomEvent('modal:setTitle', { detail: { id: 'main-modal', title: 'Updated title' } })
// )
</script>

<template>
  <div
    ref="modalRef"
    class="drawer-modal"
    :class="[
      type,
      {
        'with-padding': padding,
      },
      {
        modalSize: modalSize,
      },
    ]"
    :aria-labelledby="labelId"
    :aria-label="options?.ariaLabel || 'Modal'"
    aria-modal="true"
    role="dialog"
  >
    <div class="modal--header">
      <h2 :id="labelId" class="modal--header-title h1-visually modal--header-drag-btn">
        {{ modalTitle }}
      </h2>
      <div class="modal-buttons">
        <m-button
          class="modal--header-close-btn"
          icon="fa-regular fa-xmark"
          color="secondary"
          title="Close Modal"
          label="Close Modal"
          icon-only
          borderless
          data-testid="modal-closeBtn"
          @click="handleCloseBtnClicked"
        />
      </div>
    </div>

    <patient-banner v-if="patientId" :patient-id="patientId" secondary blue-accent no-border />
    <div v-else />

    <div
      class="modal-content"
      :class="[bgGrey ? 'medicus-grey-background' : 'medicus-white-background']"
    >
      <slot />
    </div>
  </div>
</template>
<style lang="scss">
// NB. specific to draggable modals
.modal-small,
.modal-medium,
.modal-large,
.modal-xl {
  //outline: green solid 2px;
  .drawer-modal {
    width: 100% !important;
    @media (min-width: 768px) {
      width: 100% !important;
    }
    @media (min-width: 1050px) {
      min-width: 100%;
    }
  }
}
.modal-content {
  > .m-layout-stack {
    > .m-layout-stack:last-child {
      //NB. fix final item or buttons so not right on edge of modal
      padding-bottom: 1em;
    }
  }
}

.drawer-modal {
  display: grid;
  grid-template-rows: 48px auto 1fr;
  grid-template-areas:
    "header"
    "banner"
    "content";

  justify-self: center;

  background: white;
  border-radius: 4px;

  // ENG-15801 stops all forms from being cropped on RHS when on small screen / mobile.
  @media (min-width: 768px) {
    width: 700px;
  }

  // margin: calc(40px + 7vw) auto 0;
  margin: auto;
  overflow: hidden;

  // height: 100%;
  max-height: calc(100vh - 100px);
  //max-height: 100%;
  min-height: 200px;

  box-shadow: var(--box-shadow);

  @media (min-width: 1050px) {
    min-width: 1000px;
  }

  // ENG-15801 WCAG overflow issues at 400% zoom
  @media (max-width: 768px) {
    .modal-content {
      overflow: auto;
    }
  }

  .modal--header {
    border: none;
    box-shadow: none;
    padding-bottom: 0;
    display: flex;
  }

  .modal--header-title {
    flex: 1;
  }

  &.tab {
    .modal--header {
      border: none;
      box-shadow: none;
      padding-bottom: 0;
    }

    .m-tabs {
      margin-top: 0;
      > .scroll-area {
        background: var(--grey-lightest);
      }
    }
    .q-tab-panels {
      // background: var(--grey-lightest);
      height: 100%;
    }
    .q-panel {
      display: flex;
    }
    .q-tab-panel {
      height: auto;
      flex: 1 1 auto;
      overflow: auto;
    }
    .q-tabs {
      box-shadow: inset 0px -1px 0px var(--border-colour);
    }
    .q-tab {
      box-shadow: inset 0px -1px 0px var(--border-colour);
    }
  }

  &.small {
    height: auto;
    max-height: calc(100vh - 100px);
    // this makes nearly all content visible on small screens.
    // before all forms / content were cropped at 700px
    width: 100% !important;
    // slightly arbitrary @media value due to layout shift
    @media (min-width: 700px) {
      width: 700px !important;
      min-width: 700px !important;
    }
    .modal-content {
      overflow: auto;
      &:not(.medicus-grey-background) {
        background: #fff;
      }
    }
  }
  &.medium {
    height: auto;
    max-height: calc(100vh - 100px);
    // this makes nearly all content visible on small screens.
    // before all forms / content were cropped at 700px
    width: 100% !important;
    // slightly arbitrary @media value due to layout shift
    @media (min-width: 700px) {
      width: 700px !important;
      min-width: 700px !important;
    }
    .modal-content {
      overflow: auto;
      &:not(.medicus-grey-background) {
        background: #fff;
      }
    }
  }
  &.form {
    width: 700px !important;
    min-width: 700px !important;
    .modal-content {
      overflow: auto;
    }
  }
  &.large {
    @media (min-width: 700px) {
      min-width: 1000px !important;
      max-width: 1000px !important;
    }

    height: 100%;
    .modal-content {
      overflow: auto;

      &:not(.medicus-grey-background) {
        background: #fff;
      }
    }
  }
  &.full,
  &.fullscreen {
    width: 100%;
    min-width: 100%;
    height: 100%;
    max-height: 100vh;

    .modal-content {
      overflow: auto;

      &:not(.medicus-grey-background) {
        background: #fff;
      }
    }
  }

  &.with-padding {
    .modal-content {
      padding: 0 16px;
      @media (min-width: 599px) {
        padding: 1em 2em 2em;
      }
      &:has(.m-form) {
        padding: 0;
      }
    }
  }

  .modal--header {
    grid-area: header;

    display: flex;
    align-items: center;
    justify-content: space-between;

    padding: 0 4px 0 16px;

    box-shadow: inset 0px -1px 0px var(--border-colour);

    .modal--header-close-btn {
      height: 40px;
      width: 40px;
    }

    .q-icon {
      color: var(--grey-lightest-non-text);
      font-size: 22px !important;
    }
  }

  > .modal-content {
    display: flex;
    grid-area: content;
    height: 100%;
  }

  > .encounter-patient {
    grid-area: banner;
    padding: 3px 16px;

    &:has(.badges) {
      padding-bottom: 2px;
    }
  }
}
</style>
